import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../utils/axios';
import { clearPersist } from '../persist';

const API_URL = '/api/auth/';

// Load user from localStorage
const userToken = localStorage.getItem('userToken');
const user = localStorage.getItem('user');

const initialState = {
  userInfo: user ? JSON.parse(user) : null,
  userToken: userToken || null,
  loading: false,
  error: null,
  success: false,
};

// Register user. Two modes:
// - self-serve: pass companyName, omit invitationToken (gated by
//   the global signup-allowed setting on the server).
// - invited: pass invitationToken, omit companyName (bypasses the
//   gate; user joins the inviting company at the invited role and
//   the invitation gets auto-accepted server-side).
export const registerUser = createAsyncThunk(
  'auth/register',
  async ({ username, email, password, fullName, companyName, invitationToken }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
        },
      };
      const body = { username, email, password, full_name: fullName };
      if (companyName) body.company_name = companyName;
      if (invitationToken) body.invitation_token = invitationToken;
      const { data } = await axios.post(`${API_URL}register`, body, config);
      return data || { message: 'Registration successful.' };
    } catch (error) {
      // Use the shared formatter so Pydantic v2 422 arrays don't
      // crash the Register page (React #31).
      const { formatApiError } = await import('../../utils/errorMessage');
      return rejectWithValue(formatApiError(error, 'Could not register.'));
    }
  }
);

// Login user
export const userLogin = createAsyncThunk(
  'auth/login',
  async ({ username, password }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      };
      const formData = new URLSearchParams();
      formData.append('username', username);
      formData.append('password', password);
      
      const { data } = await axios.post(`${API_URL}token`, formData, config);
      
      // Store token in localStorage
      localStorage.setItem('userToken', data.access_token);
      
      // Get user info
      axios.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
      
      const userResponse = await axios.get('/api/users/me');
      
      localStorage.setItem('user', JSON.stringify(userResponse.data));
      
      return { token: data.access_token, user: userResponse.data };
    } catch (error) {
      if (error.response && error.response.data.detail) {
        return rejectWithValue(error.response.data.detail);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      localStorage.removeItem('userToken');
      localStorage.removeItem('user');
      localStorage.removeItem('activeCompanyId');
      localStorage.removeItem('activeProduct');
      localStorage.removeItem('activeCampaignId');
      // v10.21.20: purge tus-js-client resume metadata too. Pre-fix
      // a half-finished upload's filename + size + tenant id stayed
      // readable in localStorage after logout — visible to anyone
      // who landed on /login next on the same browser. Security
      // review (item #6) called this out as a fingerprint leak.
      try {
        const tusKeys = [];
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (k && k.startsWith('tus::')) tusKeys.push(k);
        }
        tusKeys.forEach((k) => localStorage.removeItem(k));
      } catch (_e) { /* private mode / unavailable; not fatal */ }
      // Wipe the SWR persist cache too — different user / different
      // company should not see the previous session's cached data.
      try {
        clearPersist();
      } catch (_e) { /* test env without localStorage; not fatal */ }
      state.loading = false;
      state.userInfo = null;
      state.userToken = null;
      state.error = null;
    },
    setCredentials: (state, { payload }) => {
      state.userInfo = payload;
    },
    resetSuccess: (state) => {
      state.success = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Register user
    builder.addCase(registerUser.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(registerUser.fulfilled, (state) => {
      state.loading = false;
      state.success = true;
    });
    builder.addCase(registerUser.rejected, (state, { payload }) => {
      state.loading = false;
      state.error = payload;
    });
    
    // Login user
    builder.addCase(userLogin.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(userLogin.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.userInfo = payload.user;
      state.userToken = payload.token;
      state.success = true;
      // Reset workspace context on fresh login
      localStorage.removeItem('activeCompanyId');
      localStorage.removeItem('activeProduct');
    });
    builder.addCase(userLogin.rejected, (state, { payload }) => {
      state.loading = false;
      state.error = payload;
    });
  },
});

export const { logout, setCredentials, resetSuccess } = authSlice.actions;
export default authSlice.reducer;